import Vue from 'vue';
import VueCookies from 'vue-cookies';
import VueRouter from 'vue-router';
import routes from './routes';
import store from '../store';
import jwt_decode from 'jwt-decode';
import { getAccessTokenByRefreshToken } from '@/api';
import { requestPageLog } from '@/api/common';
import { isMobile } from '@/lib/Detect';

Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.onError(error => {
  if (/loading chunk .* failed./i.test(error.message)) {
    window.location.reload();
  }
});

// eslint-disable-next-line no-unused-vars
router.afterEach((to, from) => {
  if (to.meta.title) {
    // 닥플 | 대한민국 의사 커뮤니티 Docple
    Vue.nextTick(() => {
      document.title = to.meta.title;
    });
  }
});

// 페이지 진입시 refreshToken 가진 사용자 일 경우 30초 마다 refresh Token 유효한지 체크. 없으면 로그아웃 시켜버림.
const checkRefreshToken = async () => {
  // refreshToken 만료 시간 지나면 alert 후 login 페이지로 이동. 로그인 만료된 페이지 redirect=로 물고 이동
  if (!VueCookies.get('refreshToken')) {
    clearInterval(interval.data);
    store.commit('logout');
    alert('로그인이 만료되었습니다. 다시 로그인해주세요.');

    const loginUrl = `${process.env.VUE_APP_DOCPLE_PLUS_URL}/auth/login`;
    const origin = process.env.VUE_APP_URL;
    const redirectURL = `${process.env.VUE_APP_URL}${location.pathname}${location.search}`;

    location.href = `${loginUrl}?origin=${origin}&redirectURL=${redirectURL}`;
  }
};

// interval 추가용 Object
const interval = {
  data: null,
};

router.beforeEach(async (to, from, next) => {
  // 모든 페이지를 docple-plus의 동일 path로 이관 (query/hash 유지)
  // - 경로 이름이 다른 5개는 기존 매핑대로 이동
  // - 메인 페이지(/ , /index.do)는 /docple 로 이동
  // - 그 외는 동일 path
  const base = process.env.VUE_APP_DOCPLE_PLUS_URL;
  const suffix = window.location.search + window.location.hash;
  const remap = [
    ['/login', '/auth/login'],
    ['/join', '/auth/signup'],
    ['/terms', '/auth/signup/terms'],
    ['/findIdPw', '/auth/login'],
    ['/cashmall', '/mypage/cash-history'],
  ];
  const matched = remap.find(([prefix]) => to.path.startsWith(prefix));
  if (matched) {
    window.location.replace(`${base}${matched[1]}${suffix}`);
    return;
  }
  const isMainPage = to.path === '/' || to.path === '/index.do';
  const targetPath = isMainPage ? `/docple${suffix}` : to.fullPath;
  window.location.replace(`${base}${targetPath}`);
  return;

  /* eslint-disable no-unreachable */
  // ⚠️ 아래는 전체 이관 이전의 기존 진입 로직 (현재 도달하지 않음, 롤백용 보존)
  // mypage, join, terms, login, findIdPw 진입 시 외부 프로젝트로 리다이렉트
  const redirectMap = {
    '/mypage': '/mypage',
    '/login': '/auth/login',
    '/join': '/auth/signup',
    '/terms': '/auth/signup/terms',
    '/findIdPw': '/auth/login',
  };

  const matchedPrefix = Object.keys(redirectMap).find(prefix => to.path.startsWith(prefix));
  if (matchedPrefix) {
    const targetPath = redirectMap[matchedPrefix];
    let finalPath = targetPath;

    // mypage의 경우 하위 경로까지 같이 넘겨줌
    if (matchedPrefix === '/mypage') {
      const subPath = to.path.substring(matchedPrefix.length);
      finalPath = `${targetPath}${subPath}`;
    }

    const query = window.location.search;
    const externalUrl = `${process.env.VUE_APP_DOCPLE_PLUS_URL}${finalPath}${query}`;
    window.location.href = externalUrl;
    return;
  }

  // window.location.href = '/maintenance';
  if (to.meta.error) next();

  // /cashmall 경로 진입 시 docple-plus로 리다이렉트
  if (to.path.startsWith('/cashmall')) {
    window.location.href = `${process.env.VUE_APP_DOCPLE_PLUS_URL}/mypage/cash-history`;
    return;
  }

  if (VueCookies.get('refreshToken')) {
    const uid = jwt_decode(VueCookies.get('refreshToken'))?.uid;
    // accessToken 이 있을 때
    if (VueCookies.get('accessToken')) {
      if (store.getters.userId !== uid) {
        await store.dispatch('getUserInfo');
      }
      store.commit('isLoggedIn', true);
    } else {
      // accessToken 이 없을 때, getToken 용 uid 설정
      await store.commit('setUid', uid);
      await getAccessTokenByRefreshToken();
      if (store.getters.loggedIn) await store.dispatch('getUserInfo');
    }
    // 페이지 진입시 refreshToken 주기적 체크 현재 30초
    clearInterval(interval.data);
    interval.data = setInterval(checkRefreshToken, 30 * 1000);
  } else {
    // 로그아웃 후 interval 남아있는 케이스 해지
    clearInterval(interval.data);
    store.commit('logout');
    store.commit('resetState', null);
  }

  if (VueCookies.get('communityToken')) {
    store.commit('authCommunity', true);
  }

  if (to.matched.some(record => isMobile && !record.meta.useMobile)) {
    next({
      path: '/mobile/notFound', // 모바일 사용안하는 메뉴일 때 준비중 페이지
    });
  }

  if (to.matched.some(record => record.meta.requireAuth && !store.getters.loggedIn)) {
    const loginUrl = `${process.env.VUE_APP_DOCPLE_PLUS_URL}/auth/login`;
    const origin = process.env.VUE_APP_URL;
    const redirectURL = `${process.env.VUE_APP_URL}${to.fullPath}`;

    return (location.href = `${loginUrl}?origin=${origin}&redirectURL=${redirectURL}`);
  }

  if (to.path.includes('community')) {
    console.log('to.fullPath', to.fullPath);
    if (process.env.NODE_ENV === 'production') {
      window.location.href = to.fullPath;
      return;
    }
    next();
  }

  if (to.path === '/' || to.path === '/index.do') {
    // 뷰 라우트에서 메인 페이지를 찾을 경우 리액트 메인 페이지로 이동되도록
    if (process.env.NODE_ENV === 'production') {
      window.location.pathname = '/';
      return;
    }
    next();
    // if (isMobile && !store.getters.loggedIn) {
    //   next({
    //     path: '/login',
    //     query: { redirect: to.fullPath === '/' ? undefined : to.fullPath, jstl: 'N' },
    //   });
    // }

    // // mobile home url의 경우인데 pc로 온 경우
    // if (!isMobile && to.query.type) {
    //   const type = to.query.type;
    //   if (type === 'class') {
    //     if (store.getters.loggedIn) {
    //       location.href = '/class';
    //     } else {
    //       next({
    //         name: 'Login',
    //         query: { redirect: '/class', replace: 'N' },
    //       });
    //     }
    //   }
    // }
  }

  // 비회원 로그
  if (to.meta.menuid1) {
    requestPageLog({
      url: to.fullPath,
      menuid1: to.meta.menuid1,
      menuid2: to.meta.menuid2,
      menuid3: to.query?.subCode ? to.query.subCode : null,
      menuid4: to.params.id ? to.params.id : to.query.bid ? to.query.bid : null,
    });
  }
  next();

  // 커뮤니티 인증이 필요한 페이지 인데, 커뮤니티 인증이 안되어있을 때
  if (to.meta.requireCommunityAuth && !store.getters.authCommunity && !store.getters.isMangerOrHigher) {
    next({
      path: '/community/password',
      query: { redirect: to.fullPath, jstl: 'N' },
    });
  }

  if (to.meta.menuid1) {
    const { uid, joinType } = store.getters.userInfo;
    requestPageLog({
      uid,
      joinType,
      url: to.fullPath,
      menuid1: to.meta.menuid1,
      menuid2: to.meta.menuid2,
      menuid3: to.query?.subCode ? to.query.subCode : null,
      menuid4: to.params.id ? to.params.id : to.query.bid ? to.query.bid : null,
    });
  }
  next(); // 반드시 next()를 호출하십시오!
  /* eslint-enable no-unreachable */
});

// cf ) NavigationDuplicated 에러 https://velog.io/@hschoi1104/Vue.js-NavigationDuplicated-%EC%97%90%EB%9F%AC-%ED%95%B4%EA%B2%B0%ED%95%98%EA%B8%B0
const originalPush = VueRouter.prototype.push;
const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err && err.name !== 'NavigationDuplicated') throw err;
  });
};
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err;
  });
};

export default router;
